import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Save, Plus, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ClassSchedule {
  id: string;
  day_of_week: number;
  time_slot: string;
  court_number: number;
  max_students: number;
  instructor?: {
    name: string;
  };
  level?: {
    name: string;
    color: string;
  };
}

interface Booking {
  id: string;
  student_name: string;
  schedule_id: string;
  booking_date: string;
}

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
const TIME_SLOTS = ['07:00:00', '08:00:00', '09:00:00', '16:00:00', '17:00:00'];

export default function SimpleCalendarBooking() {
  const [schedules, setSchedules] = useState<ClassSchedule[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [currentWeek, setCurrentWeek] = useState<Date[]>([]);
  const [loading, setLoading] = useState(true);
  const [newStudentName, setNewStudentName] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<{scheduleId: string, date: string} | null>(null);

  useEffect(() => {
    // Configurar la semana actual comenzando desde el martes 15 de julio 2025
    const today = new Date(2025, 6, 15); // 15 de julio 2025 (mes 6 = julio)
    const weekDays = getWeekDays(today);
    setCurrentWeek(weekDays);
    
    fetchSchedules();
    fetchBookings(weekDays);
  }, []);

  const getWeekDays = (startDate: Date) => {
    const days = [];
    // Comenzar desde el lunes de la semana
    const monday = new Date(startDate);
    const dayOfWeek = startDate.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Ajustar para que lunes sea el primer día
    monday.setDate(startDate.getDate() + diff);
    
    // Generar los 5 días laborales (lunes a viernes)
    for (let i = 0; i < 5; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const fetchSchedules = async () => {
    try {
      const { data, error } = await supabase
        .from('class_schedules')
        .select(`
          *,
          instructor:instructors(name),
          level:class_levels(name, color)
        `)
        .eq('is_active', true)
        .order('day_of_week')
        .order('time_slot')
        .order('court_number');

      if (error) throw error;
      setSchedules(data || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const fetchBookings = async (weekDays: Date[]) => {
    try {
      const startDate = formatDate(weekDays[0]);
      const endDate = formatDate(weekDays[4]);

      const { data, error } = await supabase
        .from('class_bookings')
        .select('*')
        .gte('booking_date', startDate)
        .lte('booking_date', endDate)
        .eq('status', 'confirmed');

      if (error) throw error;
      setBookings(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setLoading(false);
    }
  };

  const addStudent = async (scheduleId: string, date: string, studentName: string) => {
    if (!studentName.trim()) return;

    try {
      const { error } = await supabase
        .from('class_bookings')
        .insert([{
          schedule_id: scheduleId,
          user_id: null, // Sin usuario específico para reservas públicas
          student_name: studentName.trim(),
          booking_date: date,
          status: 'confirmed'
        }]);

      if (error) throw error;

      // Actualizar bookings localmente
      fetchBookings(currentWeek);
      setNewStudentName('');
      setSelectedSlot(null);
    } catch (error: any) {
      alert('Error al agregar estudiante: ' + error.message);
    }
  };

  const removeStudent = async (bookingId: string) => {
    try {
      const { error } = await supabase
        .from('class_bookings')
        .delete()
        .eq('id', bookingId);

      if (error) throw error;
      fetchBookings(currentWeek);
    } catch (error: any) {
      alert('Error al eliminar estudiante: ' + error.message);
    }
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const formatTime = (time: string) => {
    return time.slice(0, 5);
  };

  const getBookingsForSlot = (scheduleId: string, date: string) => {
    return bookings.filter(b => 
      b.schedule_id === scheduleId && 
      b.booking_date === formatDate(date)
    );
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newStartDate = new Date(currentWeek[0]);
    newStartDate.setDate(newStartDate.getDate() + (direction === 'next' ? 7 : -7));
    const newWeek = getWeekDays(newStartDate);
    setCurrentWeek(newWeek);
    fetchBookings(newWeek);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Calendario de Clases</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigateWeek('prev')}
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
          >
            ← Semana Anterior
          </button>
          <span className="font-medium">
            {currentWeek[0]?.toLocaleDateString('es-ES', { 
              day: 'numeric', 
              month: 'long' 
            })} - {currentWeek[4]?.toLocaleDateString('es-ES', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}
          </span>
          <button
            onClick={() => navigateWeek('next')}
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
          >
            Semana Siguiente →
          </button>
        </div>
      </div>

      {/* Calendario Semanal */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Encabezados de días */}
          <div className="grid grid-cols-6 gap-2 mb-4">
            <div className="font-semibold text-center py-2">Horario</div>
            {currentWeek.map((date, index) => (
              <div key={index} className="font-semibold text-center py-2 bg-blue-50 rounded">
                <div>{DAYS[index]}</div>
                <div className="text-sm text-gray-600">
                  {date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                </div>
              </div>
            ))}
          </div>

          {/* Filas de horarios */}
          {TIME_SLOTS.map((timeSlot) => (
            <div key={timeSlot} className="grid grid-cols-6 gap-2 mb-4">
              {/* Columna de horario */}
              <div className="flex items-center justify-center bg-gray-50 rounded p-2">
                <Clock className="h-4 w-4 mr-2 text-gray-500" />
                <span className="font-medium">{formatTime(timeSlot)}</span>
              </div>

              {/* Columnas de días */}
              {currentWeek.map((date, dayIndex) => {
                const daySchedules = schedules.filter(s => 
                  s.day_of_week === dayIndex + 1 && 
                  s.time_slot === timeSlot
                );

                return (
                  <div key={dayIndex} className="space-y-2">
                    {daySchedules.map((schedule) => {
                      const slotBookings = getBookingsForSlot(schedule.id, date);
                      const availableSpots = schedule.max_students - slotBookings.length;

                      return (
                        <div
                          key={schedule.id}
                          className="border rounded-lg p-3 bg-white hover:bg-gray-50 min-h-[120px]"
                        >
                          {/* Header de la clase */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-1">
                              <MapPin className="h-3 w-3 text-gray-500" />
                              <span className="text-xs font-medium">C{schedule.court_number}</span>
                              {schedule.level && (
                                <span
                                  className="px-1 py-0.5 text-xs rounded text-white"
                                  style={{ backgroundColor: schedule.level.color }}
                                >
                                  {schedule.level.name.charAt(0)}
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">
                              {availableSpots}/4
                            </span>
                          </div>

                          {/* Lista de estudiantes */}
                          <div className="space-y-1 mb-2">
                            {slotBookings.map((booking, index) => (
                              <div
                                key={booking.id}
                                className="flex items-center justify-between bg-blue-50 rounded px-2 py-1"
                              >
                                <span className="text-sm">{booking.student_name}</span>
                                <button
                                  onClick={() => removeStudent(booking.id)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                            
                            {/* Espacios vacíos */}
                            {Array.from({ length: availableSpots }).map((_, index) => (
                              <div
                                key={`empty-${index}`}
                                className="border-2 border-dashed border-gray-200 rounded px-2 py-1 text-center text-gray-400 text-sm"
                              >
                                Disponible
                              </div>
                            ))}
                          </div>

                          {/* Botón para agregar estudiante */}
                          {availableSpots > 0 && (
                            <div>
                              {selectedSlot?.scheduleId === schedule.id && 
                               selectedSlot?.date === formatDate(date) ? (
                                <div className="flex space-x-1">
                                  <input
                                    type="text"
                                    value={newStudentName}
                                    onChange={(e) => setNewStudentName(e.target.value)}
                                    placeholder="Nombre del alumno"
                                    className="flex-1 text-xs border rounded px-2 py-1"
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        addStudent(schedule.id, formatDate(date), newStudentName);
                                      }
                                    }}
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => addStudent(schedule.id, formatDate(date), newStudentName)}
                                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                  >
                                    <Save className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedSlot(null);
                                      setNewStudentName('');
                                    }}
                                    className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setSelectedSlot({
                                    scheduleId: schedule.id,
                                    date: formatDate(date)
                                  })}
                                  className="w-full flex items-center justify-center space-x-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                >
                                  <Plus className="h-3 w-3" />
                                  <span>Agregar</span>
                                </button>
                              )}
                            </div>
                          )}

                          {availableSpots === 0 && (
                            <div className="text-center text-xs text-red-600 font-medium">
                              LLENO
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Leyenda */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium mb-2">Leyenda:</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4 text-gray-500" />
            <span>C1/C2 = Cancha 1/2</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span>P = Principiante</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span>I = Intermedio</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span>A = Avanzado</span>
          </div>
        </div>
      </div>
    </div>
  );
}